/* ──────────────────────────────────────────────────────────────
   src/components/UserDialog.tsx
   Diálogo alta / edición de usuarios
   ────────────────────────────────────────────────────────────── */
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Button,
  Switch,
  FormControlLabel,
  MenuItem,
} from '@mui/material';
import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useIsManager } from '../hooks/useDepartamentoManagers';
import { DepartamentoRol } from '../api/departamentoManagers';

/* -------- tipos -------- */
export interface UserForm {
  email: string;
  nombre?: string;
  apellidos?: string;
  rol:
    | 'profesor'
    | 'admin'
    | 'att_familias'
    | 'admisiones'
    | 'cliente'
    | 'att_alumnado'
    | 'att_profesorado'
    | 'facturacion'
    | 'hr'
    | 'consultor';
  activo: boolean;
  /* nuevos */
  fechaIncorporacion: string; // ISO YYYY-MM-DD, obligatorio si rol ≠ cliente
  password?: string;
  passwordConfirm?: string;
  esManager?: boolean; // Si el usuario es manager de su departamento
}

export default function UserDialog({
  open,
  initial,
  onClose,
  onSave,
  onDelete,
}: {
  open: boolean;
  initial?: Partial<UserForm> | any; // <- aceptamos también snake_case
  onClose: () => void;
  onSave: (data: UserForm) => void;
  onDelete?: () => void;
}) {
  const creating = !initial;
  const { t } = useTranslation<'translation'>();
  
  // Obtener el ID del usuario si estamos editando
  const usuarioId = (initial as any)?.id;
  const rolActual = initial?.rol;
  
  // Roles del staff que pueden ser managers (excluyendo admin)
  const staffRoles: DepartamentoRol[] = [
    'facturacion',
    'att_familias',
    'att_alumnado',
    'att_profesorado',
    'admisiones',
    'hr',
  ];
  
  const puedeSerManager = rolActual && staffRoles.includes(rolActual as DepartamentoRol);
  
  // Verificar si el usuario es manager de su departamento
  const { isManager: esManagerActual } = useIsManager(
    usuarioId,
    puedeSerManager ? (rolActual as DepartamentoRol) : undefined,
  );

  /* ── form ────────────────────────────────────────────── */
  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors },
  } = useForm<UserForm>({
    defaultValues: {
      email: '',
      nombre: '',
      apellidos: '',
      rol: 'profesor',
      activo: true,
      fechaIncorporacion: '',
      password: '',
      passwordConfirm: '',
      esManager: false,
    },
  });

  // util: normaliza cualquier fecha a YYYY-MM-DD (para <input type="date" />)
  const toYMD = (v: any): string => {
    if (!v) return '';
    if (typeof v === 'string') {
      const m = v.match(/^\d{4}-\d{2}-\d{2}/);
      if (m) return m[0]; // "YYYY-MM-DD"
    }
    try {
      const d = new Date(v);
      if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    } catch {}
    return '';
  };

  /* reset cuando cambian props */
  useEffect(() => {
    const fechaRaw =
      (initial as any)?.fechaIncorporacion ?? (initial as any)?.fecha_incorporacion ?? null;

    reset({
      email: initial?.email ?? '',
      nombre: initial?.nombre ?? '',
      apellidos: initial?.apellidos ?? '',
      rol: initial?.rol ?? 'profesor',
      activo: (initial as any)?.activo ?? true,
      fechaIncorporacion: toYMD(fechaRaw),
      esManager: esManagerActual ?? false,
    });
  }, [initial, reset, esManagerActual]);

  const pwd = watch('password');
  const rol = watch('rol'); // para esconder el campo a clientes

  const submit = handleSubmit((data) => onSave(data));

  /* ── UI ──────────────────────────────────────────────── */
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        {creating ? t('user.dialog.title.new') : t('user.dialog.title.edit')}
      </DialogTitle>

      <form onSubmit={submit}>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            {/* e-mail */}
            <TextField
              label={t('user.dialog.field.email.label')}
              fullWidth
              {...register('email', { required: true })}
              error={!!errors.email}
              helperText={errors.email && t('user.dialog.field.email.required')}
            />

            {/* nombre / apellidos */}
            <Stack direction="row" spacing={1}>
              <TextField
                fullWidth
                label={t('user.dialog.field.firstName.label')}
                {...register('nombre')}
              />
              <TextField
                fullWidth
                label={t('user.dialog.field.lastName.label')}
                {...register('apellidos')}
              />
            </Stack>

            {/* fecha de incorporación (oculta para clientes) */}
            {rol !== 'cliente' && (
              <TextField
                type="date"
                label={t('user.dialog.field.fechaIncorporacion.label')}
                InputLabelProps={{ shrink: true }}
                fullWidth
                {...register('fechaIncorporacion', { required: true })}
                error={!!errors.fechaIncorporacion}
                helperText={
                  errors.fechaIncorporacion && t('user.dialog.field.fechaIncorporacion.required')
                }
              />
            )}

            {/* contraseña sólo al crear */}
            {creating && (
              <>
                <TextField
                  label={t('user.dialog.field.password.label')}
                  type="password"
                  fullWidth
                  {...register('password', { required: true, minLength: 6 })}
                  error={!!errors.password}
                  helperText={errors.password && t('user.dialog.field.password.minLength')}
                />
                <TextField
                  label={t('user.dialog.field.passwordConfirm.label')}
                  type="password"
                  fullWidth
                  {...register('passwordConfirm', {
                    required: true,
                    validate: (v) =>
                      v === pwd ? true : t<string>('user.dialog.field.passwordConfirm.noMatch'),
                  })}
                  error={!!errors.passwordConfirm}
                  helperText={errors.passwordConfirm?.message}
                />
              </>
            )}

            {/* rol */}
            <Controller
              name="rol"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField select fullWidth label={t('userDialog.field.rol')} {...field}>
                  <MenuItem value="profesor">
                    {t('user.dialog.field.role.options.profesor')}
                  </MenuItem>
                  <MenuItem value="admin">{t('user.dialog.field.role.options.admin')}</MenuItem>
                  <MenuItem value="att_familias">
                    {t('user.dialog.field.role.options.att_familias')}
                  </MenuItem>
                  <MenuItem value="att_alumnado">
                    {t('user.dialog.field.role.options.att_alumnado')}
                  </MenuItem>
                  <MenuItem value="att_profesorado">
                    {t('user.dialog.field.role.options.att_profesorado')}
                  </MenuItem>
                  <MenuItem value="admisiones">
                    {t('user.dialog.field.role.options.admisiones')}
                  </MenuItem>
                  <MenuItem value="facturacion">
                    {t('user.dialog.field.role.options.facturacion')}
                  </MenuItem>
                  <MenuItem value="hr">{t('user.dialog.field.role.options.hr')}</MenuItem>
                  <MenuItem value="consultor">
                    {t('user.dialog.field.role.options.consultor')}
                  </MenuItem>
                </TextField>
              )}
            />

            {/* activo switch */}
            <Controller
              name="activo"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  label={t('user.dialog.field.active.label')}
                  control={
                    <Switch checked={!!field.value} onChange={(_, v) => field.onChange(v)} />
                  }
                />
              )}
            />

            {/* Manager switch - solo para roles del staff (excepto admin) */}
            {puedeSerManager && (
              <Controller
                name="esManager"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    label="Manager del departamento"
                    control={
                      <Switch checked={!!field.value} onChange={(_, v) => field.onChange(v)} />
                    }
                  />
                )}
              />
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          {initial && onDelete && (
            <Button color="error" onClick={onDelete}>
              {t('user.dialog.actions.delete')}
            </Button>
          )}
          <Button onClick={onClose}>{t('user.dialog.actions.cancel')}</Button>
          <Button variant="contained" type="submit">
            {creating ? t('user.dialog.actions.create') : t('user.dialog.actions.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
